import { all, put, call, fork, takeLatest, select } from 'redux-saga/effects'
import { AnyAction } from 'redux'

import {
  updateChatSessions,
  setStoppingState,
  setCurrentSessionCode,
  updateSelectedAgent,
  addChatContext,
  clearChatContext,
  setSessionHistory
} from './actions'
import {
  AgentActionTypes,
  AgentChatRole,
  AgentCode,
  AgentStatus,
  ChatSession,
  ContextUsage
} from './types'
import { enqueue } from 'common/notifier'
import {
  ComponentContext,
  COMPACT_COMMAND,
  ContextType,
  getChatSessionCode,
  mapSessionMessages
} from 'pages/app/components/agents/helpers'
import { aiApi } from 'services/ai'
import { ApplicationState } from 'store'
import { SessionSummary } from './types'

const sanitizeChatContext = (context?: Record<string, any>) => {
  if (!context) {
    return context
  }

  return {
    ...context,
    component_list: context.component_list?.map(
      ({ pinned, ...component }: ComponentContext) => component
    )
  }
}

function* handleSetSelectedAgent({ payload }: AnyAction) {
  try {
    let chatSessions = ((state: ApplicationState) => state.agent.sessions)(yield select())
    const selectedAgent = ((state: ApplicationState) => state.agent.selectedAgent)(yield select())
    const { agent, context } = payload
    const agentCode = agent.code as AgentCode
    if (selectedAgent?.code === agentCode) {
      return
    }

    yield put(updateSelectedAgent(agent))
    const chatSessionCode = getChatSessionCode(agentCode)
    if (!chatSessions) {
      chatSessions = {}
    }
    if (!chatSessions[chatSessionCode]) {
      // create new session
      const newSession: ChatSession = {
        chatSessionId: `session_${new Date().getTime()}`,
        chatSessionCode,
        agentCode,
        messages: [],
        draftMessage: ''
      }
      chatSessions = { ...chatSessions, [chatSessionCode]: newSession }
      yield put(updateChatSessions(chatSessions))
    }
    yield put(setCurrentSessionCode(chatSessionCode))
    if (context) {
      const { contextType, value } = context
      yield put(
        addChatContext(agentCode, contextType, value, {
          isSingle: agent?.context_settings?.max_components === 1
        })
      )
    }
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleChatWithAgent({ payload }: AnyAction) {
  try {
    let chatSessions = ((state: ApplicationState) => state.agent.sessions)(yield select())
    const selectedAgent = ((state: ApplicationState) => state.agent.selectedAgent)(yield select())
    const model = ((state: ApplicationState) => state.app.model)(yield select())
    const selectedNodeIds = ((state: ApplicationState) => state.app.model.selectedNodeIds)(
      yield select()
    )

    if (!selectedAgent) {
      throw new Error('No agent selected')
    }
    const currentSessionCode = ((state: ApplicationState) => state.agent.currentSessionCode)(
      yield select()
    )
    const agentCode = selectedAgent.code
    const sessionCode = currentSessionCode || getChatSessionCode(agentCode as AgentCode)
    if (!chatSessions || !chatSessions[sessionCode]) {
      throw new Error('No chat session found')
    }
    let chatSession = chatSessions[sessionCode]
    const { chatMessage, metadata } = payload

    // adds common params to metadata
    const newMetadata = metadata ? { ...metadata } : {}
    newMetadata.url = window.location.href

    // Add selected node Ids to metadata
    if (selectedNodeIds && selectedNodeIds.length > 0) {
      newMetadata.selected_nodes = selectedNodeIds
    } else {
      newMetadata.selected_nodes = model?.selectedNode?.identifier
        ? [model?.selectedNode?.identifier]
        : []
    }

    newMetadata.module_id = model?.diagram?.moduleId

    if (newMetadata.url.includes('/code')) {
      newMetadata.definition = model?.selectedNode?.codeNode?.definition
    }

    // append user message
    const updatedMessages = [...(chatSession.messages || []), chatMessage]
    chatSession = {
      ...chatSession,
      messages: updatedMessages,
      metadata: newMetadata,
      draftMessage: ''
    }
    chatSessions = { ...chatSessions, [sessionCode]: chatSession }
    yield put(updateChatSessions(chatSessions))

    // Snapshot the context for this request before clearing one-shot attachments
    // (uploaded files must not be resent on subsequent turns, see PP-3417).
    const contextForRequest = sanitizeChatContext(chatSession.context)
    if (chatSession.context?.[ContextType.FILE]?.length) {
      yield put(clearChatContext(agentCode, [ContextType.FILE]))
    }

    // call API
    yield call(
      aiApi.chat,
      agentCode,
      chatMessage,
      chatSession.chatSessionId,
      newMetadata,
      contextForRequest
    )
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleClearSession({ payload }: AnyAction) {
  try {
    const agentCode = payload
    let chatSessions = ((state: ApplicationState) => state.agent.sessions)(yield select())
    const currentSessionCode = ((state: ApplicationState) => state.agent.currentSessionCode)(
      yield select()
    )
    const sessionCode = currentSessionCode || getChatSessionCode(agentCode as AgentCode)
    if (!chatSessions || !chatSessions[sessionCode]) {
      throw new Error('No chat session found')
    }
    let chatSession = chatSessions[sessionCode]
    // clear messages
    chatSession = {
      ...chatSession,
      chatSessionId: `session_${new Date().getTime()}`,
      messages: [],
      draftMessage: '',
      status: undefined,
      metadata: undefined,
      contextUsage: undefined
    }
    chatSessions = { ...chatSessions, [sessionCode]: chatSession }
    yield put(updateChatSessions(chatSessions))
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleCompactSession({ payload }: AnyAction): Generator {
  const { agentCode, chatSessionId } = payload
  const chatSessions = ((state: ApplicationState) => state.agent.sessions)(
    (yield select()) as ApplicationState
  )
  const sessionCode =
    ((state: ApplicationState) => state.agent.currentSessionCode)(
      (yield select()) as ApplicationState
    ) || getChatSessionCode(agentCode as AgentCode)
  const chatSession = chatSessions?.[sessionCode]
  if (!chatSessionId || !chatSession) {
    return
  }
  try {
    // show the actual command in the chat and lock the input while
    // compacting; the API streams COMPACTING/COMPLETED over the WS
    const updatedSession: ChatSession = {
      ...chatSession,
      messages: [
        ...(chatSession.messages || []),
        { role: AgentChatRole.user, content: COMPACT_COMMAND }
      ],
      status: AgentStatus.COMPACTING
    }
    yield put(updateChatSessions({ ...chatSessions, [sessionCode]: updatedSession }))
    yield call(aiApi.chat, agentCode, { role: 'user', content: COMPACT_COMMAND }, chatSessionId)
  } catch (error: any) {
    yield put(updateChatSessions({ ...chatSessions, [sessionCode]: chatSession }))
    yield put(enqueue(error))
  }
}

function* handleClearAllSessions() {
  try {
    const chatSessions = ((state: ApplicationState) => state.agent.sessions)(yield select())

    if (!chatSessions) {
      return
    }

    const clearedSessions = Object.entries(chatSessions).reduce(
      (sessions, [sessionCode, chatSession]) => ({
        ...sessions,
        [sessionCode]: {
          ...chatSession,
          chatSessionId: `session_${new Date().getTime()}_${sessionCode}`,
          context: undefined,
          messages: [],
          draftMessage: '',
          status: undefined,
          metadata: undefined,
          contextUsage: undefined,
          isStopping: false,
          reasoningText: '',
          toolName: ''
        }
      }),
      {}
    )

    yield put(updateChatSessions(clearedSessions))
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleStopAgent({ payload }: AnyAction) {
  try {
    const { agentCode, chatSessionId } = payload
    const currentSessionCode = ((state: ApplicationState) => state.agent.currentSessionCode)(
      yield select()
    )
    const sessionCode = currentSessionCode || getChatSessionCode(agentCode as AgentCode)

    // Set stopping state to true immediately for UI feedback
    yield put(setStoppingState(agentCode, true))

    // Call API to stop agent execution on the backend
    yield call(aiApi.stopAgent, agentCode, chatSessionId)

    // Update agent status to completed and reset stopping state
    let chatSessions = ((state: ApplicationState) => state.agent.sessions)(yield select())
    if (chatSessions && chatSessions[sessionCode]) {
      let chatSession = chatSessions[sessionCode]
      if (chatSession.chatSessionId === chatSessionId) {
        chatSession = { ...chatSession, status: AgentStatus.COMPLETED }
        chatSessions = { ...chatSessions, [sessionCode]: chatSession }
        yield put(updateChatSessions(chatSessions))
      }
    }
  } catch (error: any) {
    // Reset stopping state on error
    yield put(setStoppingState(payload.agentCode, false))
    yield put(enqueue(error))
  }
}

function* handleLoadSessionHistory({ payload }: AnyAction): Generator {
  try {
    const agentCode = payload as string
    const sessions: SessionSummary[] = (yield call(
      aiApi.getSessions,
      agentCode
    )) as SessionSummary[]
    yield put(setSessionHistory(sessions))
  } catch (error: any) {
    yield put(setSessionHistory([]))
    yield put(enqueue(error))
  }
}

function* handleResumeSession({ payload }: AnyAction): Generator {
  try {
    const { agentCode, chatSessionId } = payload
    const response = (yield call(aiApi.getSessionMessages, agentCode, chatSessionId)) as {
      messages: { role: string; content: string }[]
      context_usage: ContextUsage | null
    }
    // tolerate the legacy shape (plain message list) during rolling deploys
    const messages = Array.isArray(response) ? response : response?.messages || []
    const contextUsage = Array.isArray(response) ? undefined : response?.context_usage
    let chatSessions = ((state: ApplicationState) => state.agent.sessions)(
      (yield select()) as ApplicationState
    )
    const sessionCode =
      ((state: ApplicationState) => state.agent.currentSessionCode)(
        (yield select()) as ApplicationState
      ) || getChatSessionCode(agentCode as AgentCode)

    const mappedMessages = mapSessionMessages(messages)

    const resumedSession: ChatSession = {
      ...(chatSessions?.[sessionCode] || {}),
      chatSessionId,
      chatSessionCode: sessionCode,
      agentCode: agentCode as AgentCode,
      messages: mappedMessages,
      draftMessage: '',
      status: AgentStatus.COMPLETED,
      contextUsage: contextUsage || undefined
    }
    chatSessions = { ...(chatSessions || {}), [sessionCode]: resumedSession }
    yield put(updateChatSessions(chatSessions))
    yield put(setCurrentSessionCode(sessionCode))
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleRestoreCheckpoint({ payload }: AnyAction): Generator {
  try {
    const { agentCode, chatSessionId, checkpointIndex, draftMessage, interfaceId } = payload
    const messages = (yield call(
      aiApi.restoreCheckpoint,
      agentCode,
      chatSessionId,
      checkpointIndex,
      interfaceId
    )) as {
      role: string
      content: string
    }[]

    let chatSessions = ((state: ApplicationState) => state.agent.sessions)(
      (yield select()) as ApplicationState
    )
    const sessionCode =
      ((state: ApplicationState) => state.agent.currentSessionCode)(
        (yield select()) as ApplicationState
      ) || getChatSessionCode(agentCode as AgentCode)

    const restoredSession: ChatSession = {
      ...(chatSessions?.[sessionCode] || {}),
      chatSessionId,
      chatSessionCode: sessionCode,
      agentCode: agentCode as AgentCode,
      messages: mapSessionMessages(messages),
      draftMessage,
      status: AgentStatus.COMPLETED,
      isStopping: false,
      reasoningText: '',
      toolName: ''
    }

    chatSessions = { ...(chatSessions || {}), [sessionCode]: restoredSession }
    yield put(updateChatSessions(chatSessions))

    const sessions: SessionSummary[] = (yield call(
      aiApi.getSessions,
      agentCode
    )) as SessionSummary[]
    yield put(setSessionHistory(sessions))
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleDeleteSession({ payload }: AnyAction): Generator {
  try {
    const { agentCode, chatSessionId } = payload

    const previousHistory = ((state: ApplicationState) => state.agent.sessionHistory)(
      (yield select()) as ApplicationState
    )
    if (previousHistory) {
      const optimisticHistory = previousHistory.filter(
        (session) => session.chat_session_id !== chatSessionId
      )
      yield put(setSessionHistory(optimisticHistory))
    }

    yield call(aiApi.deleteSession, agentCode, chatSessionId)

    let chatSessions = ((state: ApplicationState) => state.agent.sessions)(
      (yield select()) as ApplicationState
    )
    if (chatSessions) {
      let hasChanges = false
      const updatedSessions = { ...chatSessions }

      Object.entries(chatSessions).forEach(([sessionCode, session]) => {
        if (session?.agentCode === agentCode && session?.chatSessionId === chatSessionId) {
          updatedSessions[sessionCode] = {
            ...session,
            chatSessionId: `session_${new Date().getTime()}_${sessionCode}`,
            messages: [],
            draftMessage: '',
            status: undefined,
            metadata: undefined,
            isStopping: false,
            reasoningText: '',
            toolName: ''
          }
          hasChanges = true
        }
      })

      if (hasChanges) {
        yield put(updateChatSessions(updatedSessions))
      }
    }

    // Reload the session list
    const sessions: SessionSummary[] = (yield call(
      aiApi.getSessions,
      agentCode
    )) as SessionSummary[]
    yield put(setSessionHistory(sessions))
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleRenameSession({ payload }: AnyAction): Generator {
  try {
    const { agentCode, chatSessionId, title } = payload
    yield call(aiApi.renameSession, agentCode, chatSessionId, title)
    // Reload the session list
    const sessions: SessionSummary[] = (yield call(
      aiApi.getSessions,
      agentCode
    )) as SessionSummary[]
    yield put(setSessionHistory(sessions))
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* watchGeneralActions() {
  yield all([
    takeLatest(AgentActionTypes.SET_SELECTED_AGENT, handleSetSelectedAgent),
    takeLatest(AgentActionTypes.CHAT_WITH_AGENT, handleChatWithAgent),
    takeLatest(AgentActionTypes.STOP_AGENT, handleStopAgent),
    takeLatest(AgentActionTypes.CLEAR_SESSION, handleClearSession),
    takeLatest(AgentActionTypes.COMPACT_SESSION, handleCompactSession),
    takeLatest(AgentActionTypes.CLEAR_ALL_SESSIONS, handleClearAllSessions),
    takeLatest(AgentActionTypes.LOAD_SESSION_HISTORY, handleLoadSessionHistory),
    takeLatest(AgentActionTypes.RESUME_SESSION, handleResumeSession),
    takeLatest(AgentActionTypes.DELETE_SESSION, handleDeleteSession),
    takeLatest(AgentActionTypes.RENAME_SESSION, handleRenameSession),
    takeLatest(AgentActionTypes.RESTORE_CHECKPOINT, handleRestoreCheckpoint)
  ])
  yield all([])
}

function* agentSaga() {
  yield all([fork(watchGeneralActions)])
}

export { agentSaga }
